import React, { useState } from 'react';
import {
  X,
  ShieldCheck,
  Building2,
  Users,
  KeyRound,
  CheckCircle2,
  AlertTriangle,
  Lock,
  Unlock,
  Sparkles,
  Zap,
  Calendar,
  Clock,
  ShieldAlert,
  ArrowRight
} from 'lucide-react';
import { ClinicalBusiness, UserProfile, LicenseTier, UserRole } from '../types';

interface AccessManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  businesses: ClinicalBusiness[];
  users: UserProfile[];
  currentBusiness: ClinicalBusiness;
  currentUser: UserProfile;
  onSelectBusiness: (biz: ClinicalBusiness) => void;
  onSelectUser: (user: UserProfile) => void;
  onUpgradeLicense: (bizId: string, newKey: string, tier: LicenseTier) => void;
}

export const AccessManagementModal: React.FC<AccessManagementModalProps> = ({
  isOpen,
  onClose,
  businesses,
  users,
  currentBusiness,
  currentUser,
  onSelectBusiness,
  onSelectUser,
  onUpgradeLicense
}) => {
  const [activeTab, setActiveTab] = useState<'profile' | 'businesses' | 'license'>('profile');
  const [licenseInputKey, setLicenseInputKey] = useState<string>('');
  const [upgradeSuccessMessage, setUpgradeSuccessMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleApplyLicenseKey = () => {
    const key = licenseInputKey.trim().toUpperCase();
    if (key.includes('ENT') || key.includes('ENTERPRISE')) {
      onUpgradeLicense(currentBusiness.id, key, 'ENTERPRISE_UNIFIED');
      setUpgradeSuccessMessage('Enterprise Unified License activated! All restrictions lifted.');
    } else if (key.includes('PRO') || key.includes('COMMERCIAL')) {
      onUpgradeLicense(currentBusiness.id, key, 'COMMERCIAL_PRO');
      setUpgradeSuccessMessage('Commercial Pro License activated! Ambient sensors and RPM enabled.');
    } else if (key.includes('BETA') || key.includes('TRIAL')) {
      onUpgradeLicense(currentBusiness.id, key, 'BETA_TRIAL');
      setUpgradeSuccessMessage('Beta Trial extended by 30 days.');
    } else {
      onUpgradeLicense(currentBusiness.id, key || 'MC-PAID-AUTHORIZED-2025', 'ENTERPRISE_UNIFIED');
      setUpgradeSuccessMessage('Authorized License successfully verified and applied.');
    }

    setTimeout(() => {
      setUpgradeSuccessMessage(null);
    }, 4000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border-2 border-slate-700 rounded-3xl w-full max-w-3xl shadow-2xl overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between bg-slate-950/90">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-gradient-to-tr from-teal-500/20 to-indigo-500/20 border border-teal-500/40 text-teal-300">
              <ShieldCheck className="h-6 w-6 text-teal-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-extrabold text-white text-base tracking-wide">
                  Clinical Access & Account Management
                </h3>
                <span
                  className={`text-[10px] font-mono px-2 py-0.5 rounded font-bold uppercase ${
                    currentBusiness.licenseTier === 'ENTERPRISE_UNIFIED'
                      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                      : currentBusiness.licenseTier === 'COMMERCIAL_PRO'
                      ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                      : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                  }`}
                >
                  {currentBusiness.licenseTier.replace('_', ' ')}
                </span>
              </div>
              <p className="text-[11px] text-slate-400">
                Multi-Tenant Workspace & Role-Based Access Control (RBAC)
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex items-center px-6 pt-3 border-b border-slate-800 bg-slate-950/50 gap-4 text-xs font-semibold">
          <button
            onClick={() => setActiveTab('profile')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 ${
              activeTab === 'profile'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>Active Clinician Profile</span>
          </button>

          <button
            onClick={() => setActiveTab('businesses')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 ${
              activeTab === 'businesses'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <Building2 className="h-4 w-4" />
            <span>Switch Business Entity</span>
          </button>

          <button
            onClick={() => setActiveTab('license')}
            className={`pb-3 border-b-2 transition flex items-center gap-2 ${
              activeTab === 'license'
                ? 'border-teal-400 text-teal-300'
                : 'border-transparent text-slate-400 hover:text-slate-200'
            }`}
          >
            <KeyRound className="h-4 w-4" />
            <span>Subscription & Beta Licensing</span>
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-6 overflow-y-auto max-h-[68vh]">
          {/* TAB 1: USER PROFILE & PERMISSIONS */}
          {activeTab === 'profile' && (
            <div className="space-y-5">
              <div className="flex items-center justify-between p-4 rounded-2xl bg-slate-950 border border-slate-800">
                <div className="flex items-center gap-4">
                  <div className="h-14 w-14 rounded-2xl bg-gradient-to-tr from-teal-600 to-indigo-600 flex items-center justify-center text-white font-extrabold text-xl shadow-lg ring-2 ring-teal-500/40">
                    {currentUser.avatarInitials}
                  </div>
                  <div>
                    <h4 className="text-base font-extrabold text-white">{currentUser.fullName}</h4>
                    <p className="text-xs text-teal-400 font-medium">{currentUser.title}</p>
                    <p className="text-[11px] font-mono text-slate-400 mt-0.5">{currentUser.email}</p>
                  </div>
                </div>

                <div className="text-right">
                  <span className="text-[10px] font-mono text-slate-500 uppercase block">Assigned Role</span>
                  <span className="text-xs font-mono font-bold text-teal-300 bg-slate-900 px-2 py-1 rounded-lg border border-slate-800 mt-1 inline-block">
                    {currentUser.role}
                  </span>
                </div>
              </div>

              {/* Persona Switcher for Quick Demos */}
              <div>
                <span className="text-xs font-bold text-slate-300 block mb-2 font-mono uppercase">
                  Select User Persona (Demo Authorization):
                </span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {users.map((u) => {
                    const isSelected = u.id === currentUser.id;
                    return (
                      <div
                        key={u.id}
                        onClick={() => onSelectUser(u)}
                        className={`p-3 rounded-2xl border transition cursor-pointer flex items-center justify-between ${
                          isSelected
                            ? 'bg-teal-950/30 border-teal-500 shadow-md ring-1 ring-teal-500/50'
                            : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                        }`}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-full bg-slate-800 text-teal-300 font-bold text-xs flex items-center justify-center">
                            {u.avatarInitials}
                          </div>
                          <div>
                            <div className="text-xs font-bold text-slate-200">{u.fullName}</div>
                            <div className="text-[10px] text-slate-400">{u.role.replace(/_/g, ' ')}</div>
                          </div>
                        </div>
                        {isSelected && <CheckCircle2 className="h-4 w-4 text-teal-400" />}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Granted Security Capabilities */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800">
                <span className="text-[11px] font-mono uppercase text-slate-400 block mb-2 font-bold">
                  Active Clinical Authorization Flags:
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                  {Object.entries(currentUser.permissions).map(([perm, granted]) => (
                    <div
                      key={perm}
                      className={`p-2 rounded-xl border flex items-center gap-1.5 ${
                        granted
                          ? 'bg-emerald-950/30 border-emerald-800/50 text-emerald-300'
                          : 'bg-slate-900 border-slate-800 text-slate-500'
                      }`}
                    >
                      {granted ? <Lock className="h-3.5 w-3.5 text-emerald-400" /> : <Unlock className="h-3.5 w-3.5 text-slate-600" />}
                      <span className="text-[11px] font-mono truncate">{perm.replace(/([A-Z])/g, ' $1')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: CLINICAL BUSINESS WORKSPACE */}
          {activeTab === 'businesses' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-400">
                Switch tenant workspace to partition patients, RPM telemetry, and senior living cottages by licensed organization:
              </p>

              <div className="space-y-3">
                {businesses.map((biz) => {
                  const isCurrent = biz.id === currentBusiness.id;
                  const isTrial = biz.licenseTier === 'BETA_TRIAL';

                  return (
                    <div
                      key={biz.id}
                      onClick={() => onSelectBusiness(biz)}
                      className={`p-4 rounded-2xl border transition cursor-pointer relative overflow-hidden ${
                        isCurrent
                          ? 'bg-slate-950 border-teal-500 shadow-xl ring-1 ring-teal-500/50'
                          : 'bg-slate-950/60 border-slate-800 hover:border-slate-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h4 className="font-extrabold text-white text-sm">{biz.name}</h4>
                            <span
                              className={`text-[9px] font-mono px-2 py-0.5 rounded font-bold uppercase ${
                                biz.licenseTier === 'ENTERPRISE_UNIFIED'
                                  ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40'
                                  : biz.licenseTier === 'COMMERCIAL_PRO'
                                  ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40'
                                  : 'bg-amber-500/20 text-amber-300 border border-amber-500/40'
                              }`}
                            >
                              {biz.licenseTier.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400 mt-0.5 font-medium">{biz.facilityType}</p>
                          <p className="text-[10px] font-mono text-slate-500 mt-1">
                            NPI: {biz.activeNpi} • Max Capacity: {biz.authorizedMaxBeds} Beds
                          </p>
                        </div>

                        <div className="text-right">
                          {isTrial && (
                            <span className="text-[10px] font-mono text-amber-400 bg-amber-950/40 border border-amber-500/40 px-2 py-0.5 rounded block mb-1">
                              {biz.trialDaysRemaining} Days Left
                            </span>
                          )}
                          {isCurrent ? (
                            <span className="inline-flex items-center gap-1 text-xs font-bold text-teal-400">
                              <CheckCircle2 className="h-4 w-4" /> Active Workspace
                            </span>
                          ) : (
                            <button className="text-xs text-slate-400 hover:text-white flex items-center gap-1 font-semibold">
                              <span>Switch</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB 3: LICENSING & BETA UPGRADE */}
          {activeTab === 'license' && (
            <div className="space-y-5">
              <div className="p-4 rounded-2xl bg-gradient-to-r from-slate-950 via-slate-950 to-indigo-950/30 border border-indigo-500/30">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <KeyRound className="h-5 w-5 text-indigo-400" />
                    <span className="font-bold text-white text-sm">Active License Key:</span>
                  </div>
                  <span className="font-mono text-xs text-emerald-400 font-bold bg-slate-900 px-2.5 py-0.5 rounded-lg border border-slate-800">
                    {currentBusiness.licenseKey}
                  </span>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Authorized accounts enjoy uninterrupted access to live ambient sensors, Gemini SBAR handoffs, and real-time Medicare CPT 99454 billing validation.
                </p>
              </div>

              {/* License Key Activation Input */}
              <div className="p-4 rounded-2xl bg-slate-950 border border-slate-800 space-y-3">
                <label className="text-xs font-bold text-slate-300 font-mono uppercase block">
                  Upgrade or Enter Authorized License Key:
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={licenseInputKey}
                    onChange={(e) => setLicenseInputKey(e.target.value)}
                    placeholder="e.g., MC-ENTERPRISE-UNIFIED-2025 or MC-PRO-CARE"
                    className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs font-mono text-white placeholder-slate-500 focus:outline-none focus:border-teal-500"
                  />
                  <button
                    onClick={handleApplyLicenseKey}
                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-400 hover:to-cyan-400 text-slate-950 font-bold text-xs transition shadow-md shadow-teal-500/20"
                  >
                    Activate Key
                  </button>
                </div>

                {upgradeSuccessMessage && (
                  <div className="p-3 rounded-xl bg-emerald-950/50 border border-emerald-500 text-emerald-300 text-xs flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                    <span>{upgradeSuccessMessage}</span>
                  </div>
                )}
              </div>

              {/* Quick License Comparison */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-amber-400 font-bold block mb-1">BETA TRIAL</span>
                  <div className="font-bold text-slate-200">30-Day Sandbox</div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Up to 25 beds. Test ambient rooms & voice calls. No official billing export.
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold block mb-1">COMMERCIAL PRO</span>
                  <div className="font-bold text-slate-200">Senior Living Care</div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Up to 60 beds. Full mmWave radar fall detection, caregiver cards & SMS updates.
                  </p>
                </div>

                <div className="p-3 rounded-xl bg-slate-950 border border-teal-500/50 bg-teal-950/10">
                  <span className="text-[10px] font-mono text-teal-300 font-bold block mb-1">ENTERPRISE UNIFIED</span>
                  <div className="font-bold text-white">Full Clinical OS</div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Unlimited beds. CPT 99454 compliance ledger, legal EHR digital signatures & trauma hub.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/90 flex items-center justify-between">
          <span className="text-[11px] font-mono text-slate-500">
            MediCore Access Protocol v4.8 • Authenticated
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold transition"
          >
            Close Window
          </button>
        </div>
      </div>
    </div>
  );
};
